import { useState, useEffect } from "react";
import { NavigationButtons } from "../ui/NavigationButtons";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../../store/indexRedux";
import { resetStep1, resetApiData, clearAllPvCurtailmentCache } from "../../store/pvCurtailment";
import { fetchPortfolioServices, selectBessServices } from "../../store/companyPortfolioSlice";
import { fetchPartners } from "../../store/partnerSlice";
import { PayloadAction } from "@reduxjs/toolkit";
import { BillingEditModal } from "../addServices/BillingEditModal";
import { ContractPreviewModal, ContractSigningPayload } from "./ContractPreviewModal";
import { PvCurtailmentSuccessModal } from "./PvCurtailmentSuccessModal";
import { toast } from "react-toastify";
import { PendingToast } from "../ui/toastResponds/PendingToast";
import { ErrorToast } from "../ui/toastResponds/ErrorToast";
import axios from "axios";
import * as Sentry from "@sentry/react";
import { getSessionFromCookie } from "../utils/cognito";
import { formatNumber } from "../../utils/formatNumber";

type StepNavProps = {
  onNext?: () => void;
  onPrevious?: () => void;
  onSubmit?: () => void;
};

function formatDateToDisplay(isoOrEmpty: string | undefined | null) {
  if (!isoOrEmpty) return "-";

  const d = new Date(isoOrEmpty);
  if (Number.isNaN(d.getTime())) return isoOrEmpty;
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

function getCountryFromIBAN(code: string): string {
  const countries: { [key: string]: string } = {
    BE: "Belgium",
    NL: "Netherlands",
    DE: "Germany",
    FR: "France",
    ES: "Spain",
    IT: "Italy",
    GB: "United Kingdom",
    CH: "Switzerland",
    AT: "Austria",
    LU: "Luxembourg",
  };
  return countries[code] || "";
}

function calculateMonthlyFee(inverterPower: number | null | undefined): { value: number; display: string } {
  if (inverterPower === null || inverterPower === undefined || Number.isNaN(inverterPower)) {
    return { value: 0, display: "-" };
  }

  let fee: number;
  if (inverterPower < 250) {
    fee = 20;
  } else if (inverterPower < 500) {
    fee = 30;
  } else if (inverterPower < 1000) {
    fee = 40;
  } else {
    fee = 50;
  }

  return {
    value: fee,
    display: `${formatNumber(fee, "currency")} EUR`,
  };
}

function resolveProfitShare(
  pvPower: number | null,
  bessPower: number,
  eboilerPower: number,
  inheritedBessProfitShare: number | null,
): number {
  if (inheritedBessProfitShare !== null) return inheritedBessProfitShare;
  const pv = pvPower ?? 0;
  const total = pv + bessPower + eboilerPower;
  if (total === 0) return 20;
  const raw = (pv * 20 + bessPower * 10 + eboilerPower * 10) / total;
  return Math.min(20, Math.max(10, Math.round(raw)));
}

function ContractProp({ onPrevious }: StepNavProps) {
  const handlePrevious = onPrevious ?? (() => {});
  const dispatch = useDispatch<AppDispatch>();

  const [showBillingModal, setShowBillingModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [generatedProfitShare, setGeneratedProfitShare] = useState<string | null>(null);
  const [signedContractSnapshot, setSignedContractSnapshot] = useState<{
    siteName: string;
    companyName: string;
    startDate: string;
    endDate: string;
    monthlyFee: string;
    inverterPower: string;
    partner: string;
    controller: string;
    opportunityCost: string;
    profitShare: string;
  } | null>(null);

  const [updatedBillingInfo, setUpdatedBillingInfo] = useState<{
    email?: string;
    iban?: string;
    address?: string;
    country?: string;
  } | null>(null);

  // Read persisted wizard data
  const pv = useSelector((state: RootState) => state.pvCurtailment);
  const { partners, status } = useSelector((state: RootState) => state.partner);
  const company = useSelector((state: RootState) => state.selectedCompany.selectedUserCompany);
  const user = useSelector((state: RootState) => state.user);
  const bessServices = useSelector(selectBessServices) ?? [];
  const accessToken = useSelector((state: RootState) => state.authv2.accessToken);

  // Ensure partners are loaded when component mounts
  useEffect(() => {
    if (status === "idle" && partners.length === 0 && accessToken) {
      dispatch(fetchPartners({ token: accessToken }) as unknown as PayloadAction);
    }
  }, [dispatch, status, partners.length, accessToken]);

  const [contractId, setContractId] = useState<number | null>(null);
  const [installationId, setInstallationId] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | undefined>(undefined);
  const [signBlockedReason, setSignBlockedReason] = useState<"INJECTION_UNSIGNED" | "OFFTAKE_UNSIGNED" | null>(null);

  const handlePreviewContract = async () => {
    // The wizard can be entered with stale/wiped state (#466); without these
    // fields the backend rejects the request with 400 Missing required fields.
    if (!pv.desiredStartDate || !pv.selectedInjection?.installation_id) {
      toast(
        <ErrorToast
          title="Missing contract details"
          message="Some contract details are missing. Please go back to step 1 and walk through the steps again."
        />,
        { position: "bottom-right", autoClose: 7000, closeOnClick: true, theme: "light", icon: false },
      );
      return;
    }
    setIsPreviewLoading(true);

    // Show in-progress notification
    const toastId = toast.loading(<PendingToast title="Processing" message="Preparing contract for review..." />, {
      position: "bottom-right",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      theme: "light",
      icon: false,
      style: {
        width: "400px",
        maxWidth: "800px",
      },
    });

    try {
      const companyId =
        company?.company_id || JSON.parse(localStorage.getItem("selectedUserCompany") || "{}")?.company_id || null;

      if (!companyId) {
        throw new Error("Company ID is required");
      }

      const injectionData = pv.selectedInjection as Record<string, unknown> | null;

      const payload = {
        startDate: pv.desiredStartDate,
        endDate: pv.specifiedEndDate,
        offtakeInstallationId: pv.selectedOfftake?.installation_id || null,
        yusoCertifiedController: pv.controllerId,
        company_id: companyId,
        site_name: pv.siteName,
        site_id: injectionData?.site_id ?? null,
        type: "embedded",
        injection_installation_id: pv.selectedInjection?.installation_id || null,
        offtake_installation_id: pv.selectedOfftake?.installation_id || null,
        visible: 1,
        yuso_certified_controller: pv.controllerId,
        partner_id: pv.partnerId ? Number(pv.partnerId) : null,
        profit_share: String(resolveProfitShare(
          pv.inverterPower,
          activeBess ? (pv.selectedInjection?.bess?.nominal_power ?? 0) : 0,
          activeEboiler ? (pv.selectedInjection?.eboiler?.nominal_power ?? 0) : 0,
          inheritedBessProfitShare,
        )),
        monthly_fee: effectiveFee,
        opportunity_cost: pv.opportunityCost ?? null,
        nominal_power: pv.inverterPower ?? null,
        config: {
          test_site: true,
          revenueCalculation: false,
          yuso_certified_controller: pv.controllerId,
        },
        bess_installation_id: activeBess ? (pv.selectedInjection?.bess?.installation_id ?? null) : null,
        eboiler_installation_id: activeEboiler ? (pv.selectedInjection?.eboiler?.installation_id ?? null) : null,
      };

      const session = getSessionFromCookie();
      const token = session?.getAccessToken().getJwtToken();

      if (!token) {
        throw new Error("Authentication token is required");
      }

      const apiUrl = `${import.meta.env.VITE_API_SERVICES || "http://localhost:8001"}/api/v1/sdp/site-steering/generate`;
      const response = await axios.post(apiUrl, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      toast.dismiss(toastId);

      // Capture profit_share from backend (authoritative calculation)
      const returnedProfitShare = response.data?.profit_share ?? response.data?.response?.profit_share;
      if (returnedProfitShare) {
        setGeneratedProfitShare(String(returnedProfitShare));
      }

      // Save contract ID, installation ID, and PDF URL if returned from API
      if (response.data?.id) {
        setContractId(response.data.id);
      }
      if (response.data?.response?.installation_id || response.data?.installation_id) {
        setInstallationId(response.data?.response?.installation_id || response.data?.installation_id);
      }
      if (response.data?.pdf_url) {
        setPdfUrl(response.data.pdf_url);
      }

      const reason = response.data?.signBlockedReason;
      setSignBlockedReason(
        reason === "INJECTION_UNSIGNED" || reason === "OFFTAKE_UNSIGNED" ? reason : null,
      );

      // Show success notification
      toast.success(<PendingToast title="Success" message="Contract prepared for review!" />, {
        position: "bottom-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "light",
      });

      // Open preview modal
      setShowPreviewModal(true);

    } catch (error) {
      toast.dismiss(toastId);

      const errorMessage = error instanceof Error ? error.message : "Failed to prepare contract";
      toast.error(<PendingToast title="Error" message={errorMessage} />, {
        position: "bottom-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "light",
      });

      const axiosError = error as { response?: { status?: number; data?: unknown } };
      Sentry.captureException(error, {
        tags: { feature: "pvcurtailment", component: "ContractProp", type: "api" },
        extra: {
          endpoint: "/api/v1/sdp/site-steering/generate",
          payload: {
            company_id: company?.company_id,
            site_name: pv.siteName,
          },
          status: axiosError?.response?.status,
          data: axiosError?.response?.data,
        },
      });
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const handleSignContract = async (signatureData: ContractSigningPayload) => {
    const toastId = toast.loading(<PendingToast title="Processing" message="Signing contract..." />, {
      position: "bottom-right",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      theme: "light",
      icon: false,
      style: {
        width: "400px",
        maxWidth: "800px",
      },
    });

    try {
      const session = getSessionFromCookie();
      const token = session?.getAccessToken().getJwtToken();

      if (!token) {
        throw new Error("Authentication token is required");
      }

      // The payload is already fully constructed by the modal
      const payload = {
        ...signatureData,
        contract_id: contractId, // Add contract_id from local state
      };

      // API endpoint for signing the contract
      const apiUrl = `${import.meta.env.VITE_API_SERVICES || "http://localhost:8001"}/api/v1/sdp/site-steering/sign`;
      await axios.post(apiUrl, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      toast.dismiss(toastId);

      // Show success notification
      toast.success(<PendingToast title="Success" message="Contract signed successfully!" />, {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "light",
      });

      setShowPreviewModal(false);

      setSignedContractSnapshot({
        siteName: pv.siteName || "-",
        companyName: company?.companyName || "-",
        startDate: formatDateToDisplay(pv.desiredStartDate || ""),
        endDate: pv.contractEndType === "date" ? formatDateToDisplay(pv.specifiedEndDate || "") : "Automatic renewal",
        monthlyFee: effectiveFeeDisplay,
        inverterPower: pv.inverterPower != null ? `${formatNumber(pv.inverterPower, "power")} kVA` : "-",
        partner: partnerName,
        controller: pv.controller || "-",
        opportunityCost: pv.opportunityCost != null ? `${formatNumber(pv.opportunityCost, "feePerMwh")} €/MWh` : "-",
        profitShare: profitShareDisplay,
      });

      dispatch(resetStep1());
      dispatch(resetApiData());
      clearAllPvCurtailmentCache();

      if (company?.companyNumber && token) {
        dispatch(fetchPortfolioServices({ companyNumber: company.companyNumber, token }));
      }

      setShowSuccessModal(true);
    } catch (error) {
      toast.dismiss(toastId);

      const errorMessage = error instanceof Error ? error.message : "Failed to sign contract";
      toast.error(<PendingToast title="Error" message={errorMessage} />, {
        position: "bottom-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "light",
      });

      const axiosError = error as { response?: { status?: number; data?: unknown } };
      Sentry.captureException(error, {
        tags: { feature: "pvcurtailment", component: "ContractProp", type: "api", action: "sign" },
        extra: {
          endpoint: "/api/v1/sdp/site-steering/sign",
          installation_id: signatureData.installation_id,
          contract_id: contractId,
          company_id: signatureData.company_id,
          status: axiosError?.response?.status,
          data: axiosError?.response?.data,
        },
      });

      throw error; // Re-throw to let modal handle it
    }
  };

  const partnerName =
    pv.hasPartner === "yes"
      ? (partners.find((p) => String(p.id) === String(pv.partnerId))?.name ?? `Partner #${pv.partnerId || "-"}`) // fallback
      : pv.hasPartner === "no"
        ? "Request partner"
        : "—";

  const startDateDisplay = formatDateToDisplay(pv.desiredStartDate || "");
  // const endDisplay =
  //   pv.contractEndType === "date" ? formatDateToDisplay(pv.specifiedEndDate || "") : "Automatic renewal";

  const opportunityDisplay =
    pv.opportunityCost == null || Number.isNaN(Number(pv.opportunityCost))
      ? "-"
      : `${formatNumber(Number(pv.opportunityCost), "feePerMwh")} €/MWh`;

  const inverterDisplay =
    pv.inverterPower == null || Number.isNaN(Number(pv.inverterPower))
      ? "-"
      : `${formatNumber(Number(pv.inverterPower), "power")} kVA`;

  const selectedInjection = pv.selectedInjection;
  const powerSolar = selectedInjection ? (selectedInjection as Record<string, unknown>).power_solar : null;

  const activeBess =
    pv.selectedInjection?.hasBess === 1 && pv.selectedInjection?.bess?.has_active_contract === 1;
  const activeEboiler =
    pv.selectedInjection?.hasEboiler === 1 && pv.selectedInjection?.eboiler?.has_active_contract === 1;
  const hasCombinedServices = activeBess || activeEboiler;

  const inheritedBessProfitShare = (() => {
    if (!activeBess) return null;
    const injectionInstallationId = pv.selectedInjection?.installation_id;
    if (!injectionInstallationId) return null;
    const matchingBess = bessServices.find(
      (s) => s.injection_installation_id === injectionInstallationId,
    );
    const raw = matchingBess?.yuso_profit_share;
    const parsed = raw == null ? NaN : parseFloat(String(raw));
    return Number.isFinite(parsed) ? parsed : null;
  })();
  const pvFee = calculateMonthlyFee(pv.inverterPower);
  const bessCurrentFee = activeBess
    ? Number(pv.selectedInjection?.bess?.contract_steering_fee_fixed ?? 0)
    : 0;
  const eboilerCurrentFee = activeEboiler
    ? Number(pv.selectedInjection?.eboiler?.contract_steering_fee_fixed ?? 0)
    : 0;
  const effectiveFeeValue = Math.max(pvFee.value, bessCurrentFee, eboilerCurrentFee);
  // WIRE-FROZEN: this whole object (value + display string) is POSTed as `monthly_fee`
  // in the /generate payload above — the backend stores the display string as-is.
  // Do not reformat it; use effectiveFeeDisplay below for anything shown in the UI.
  const effectiveFee = {
    value: effectiveFeeValue,
    display: `${effectiveFeeValue},00 EUR`,
  };
  const effectiveFeeDisplay = `${formatNumber(effectiveFeeValue, "currency")} EUR`;
  const peakPowerDisplay =
    powerSolar && typeof powerSolar === "string" && !Number.isNaN(Number(powerSolar))
      ? `${formatNumber(Number(powerSolar), "power")} kWp`
      : "-";

  const companyName = company?.companyName ?? company?.companyName ?? "—";
  const companyAddress =
    updatedBillingInfo?.address ||
    (company && (company.addressStreet || company.addressNumber || company.addressZip || company.addressCity)
      ? `${company.addressStreet ?? ""} ${company.addressNumber ?? ""}\n${company.addressZip ?? ""} ${
          company.addressCity ?? ""
        }`.trim()
      : "—");
  const companyVat = company?.vatCode ?? "—";
  const companyEmail = updatedBillingInfo?.email || company?.companyEmail || company?.email || "—";
  const companyIban = updatedBillingInfo?.iban || company?.iban || "—";

  const getBillingInfoFromCompany = () => {
    if (!company) return null;

    let billingAddressStreet = company.addressStreet || "";
    let billingAddressNumber = company.addressNumber || "";
    let billingAddressZip = company.addressZip?.toString() || "";
    let billingAddressCity = company.addressCity || "";

    if (updatedBillingInfo?.address) {
      const addressParts = updatedBillingInfo.address.split("\n");
      if (addressParts.length >= 2) {
        const streetLine = addressParts[0].trim();
        const cityLine = addressParts[1].trim();
        const streetMatch = streetLine.match(/^(.+?)\s+(\d+)$/);
        if (streetMatch) {
          billingAddressStreet = streetMatch[1].trim();
          billingAddressNumber = streetMatch[2].trim();
        }
        const cityMatch = cityLine.match(/^(\d+)\s+(.+)$/);
        if (cityMatch) {
          billingAddressZip = cityMatch[1].trim();
          billingAddressCity = cityMatch[2].trim();
        }
      }
    }

    return {
      billing_id: 0,
      company_id: company.company_id || 0,
      billingEmail: updatedBillingInfo?.email || company.companyEmail || company.email || "",
      iban: updatedBillingInfo?.iban || company.iban || "",
      poNumber: null,
      twikeyRef: "",
      active_dd_mandate: 0,
      eol_dd_mandate_uploaded: 0,
      twikeyDate: null,
      twikeyCode: null,
      twikeyValid: 0,
      billingAddressStreet,
      billingAddressNumber,
      billingAddressZip,
      billingAddressCity,
      billingAddressCountry: (() => {
        if (updatedBillingInfo?.country) return updatedBillingInfo.country;

        const iban = updatedBillingInfo?.iban || company.iban || "";
        if (iban) {
          const cleanIBAN = iban.replace(/\s/g, "").toUpperCase();
          const countryCode = cleanIBAN.substring(0, 2);
          const countryName = getCountryFromIBAN(countryCode);
          if (countryName) return countryName;
        }
        return "";
      })(),
      invoicingCycle: "Monthly",
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
  };

  const handleBillingSave = (billingInfo: {
    billing_id: number;
    company_id: number;
    billingEmail: string;
    iban: string;
    poNumber: string | null;
    twikeyRef: string;
    active_dd_mandate: number;
    eol_dd_mandate_uploaded: number;
    twikeyDate: string | null;
    twikeyCode: string | null;
    twikeyValid: number;
    billingAddressStreet: string;
    billingAddressNumber: string;
    billingAddressZip: string;
    billingAddressCity: string;
    billingAddressCountry: string;
    invoicingCycle: string;
    created_at: string;
    updated_at: string;
  }) => {
    // Update local state with new billing information for immediate display
    setUpdatedBillingInfo({
      email: billingInfo.billingEmail,
      iban: billingInfo.iban,
      address:
        `${billingInfo.billingAddressStreet} ${billingInfo.billingAddressNumber}\n${billingInfo.billingAddressZip} ${billingInfo.billingAddressCity}`.trim(),
      country: billingInfo.billingAddressCountry,
    });

    // In a real implementation, you would also save this to the backend here
    // For example: await saveBillingInfo(billingInfo);

    console.log("Billing info saved:", billingInfo);
    setShowBillingModal(false);
  };

  const bessPower = activeBess ? (pv.selectedInjection?.bess?.nominal_power ?? 0) : 0;
  const eboilerPower = activeEboiler ? (pv.selectedInjection?.eboiler?.nominal_power ?? 0) : 0;
  const profitShareNumber = generatedProfitShare
    ? parseFloat(generatedProfitShare)
    : resolveProfitShare(pv.inverterPower, bessPower, eboilerPower, inheritedBessProfitShare);
  const profitShareDisplay = `${formatNumber(profitShareNumber, "profitShare")}%`;
  const profitShareRaw = generatedProfitShare ?? profitShareNumber.toFixed(5);

  const contract = {
    // Display data
    startDate: startDateDisplay,
    actualStart: pv.actualStartDate || pv.desiredStartDate || "",
    profitShare: profitShareDisplay,
    monthlyFee: effectiveFeeDisplay, // Display value with EUR
    partner: partnerName,
    peakPower: peakPowerDisplay,
    inverterPower: inverterDisplay,
    controller: pv.controller || "-",
    opportunityCost: opportunityDisplay,
    validUntil: pv.contractEndType === "date" ? formatDateToDisplay(pv.specifiedEndDate || "") : "Auto",
    company: {
      name: companyName,
      address: companyAddress,
      vat: companyVat,
      email: companyEmail,
      iban: companyIban,
    },
    // Additional fields for signing payload (raw values)
    installationId: installationId || "",
    companyId: company?.company_id || 0,
    siteName: pv.siteName || "",
    endDate: pv.specifiedEndDate || null,
    partnerId: typeof pv.partnerId === "number" ? pv.partnerId : Number(pv.partnerId) || 0,
    profitShareValue: profitShareRaw,
    monthlyFeeValue: effectiveFee.value.toFixed(5), // Raw fee for API
    steeringFeePerformancePct: "20.00000", // steering_fee_performance_pct
    inverterPowerKva: pv.inverterPower || 0,
    opportunityCostValue: pv.opportunityCost || 0,
    injectionInstallationId: pv.selectedInjection?.installation_id || "",
    offtakeInstallationId: pv.selectedOfftake?.installation_id || null,
  };

  return (
    <div>
      <div className="mb-12">
        <h1 className="text-primary-600 dark:text-white text-3xl font-semibold mb-6">Contract proposal</h1>
        <p className="text-primary-500 dark:text-gray-300 font-normal text-base text-md">
          This proposal summarizes the contract parameters you entered.
        </p>
      </div>
      <div className="bg-white dark:bg-primary-900 p-12 rounded-lg w-full border-[2px] border-gray-100 dark:border-primary-800">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <div>
              <h2 className="text-2xl font-semibold text-primary-700 dark:text-white mb-1">
                Your proposal for PV Curtailment
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Valid until {contract.validUntil === "Auto" ? "Automatic renewal" : contract.validUntil}
              </p>
            </div>
            {/* <button
              className="bg-primary-600 text-white px-6 py-3 rounded-lg font-medium flex items-center gap-2 hover:bg-primary-700 transition-colors shadow-sm"
              type="button"
            >
              <DownloadIcon className="w-5 h-5" />
              <span>Download PDF</span>
            </button> */}
          </div>
        </div>

        {/* Combined Flex Services */}
        {hasCombinedServices && (
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-primary-700 dark:text-white mb-4">Combined flex services</h3>
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-4">
              <p className="text-xs text-amber-700 dark:text-amber-400 mb-4">
                This PV Curtailment contract will be combined with your existing active flex services at this site.
              </p>
              <div className="flex flex-col gap-3">
                {activeBess && (
                  <div className="bg-white dark:bg-primary-900 border border-amber-200 dark:border-amber-700/60 rounded-lg p-4 shadow-sm">
                    <div className="flex justify-between items-center mb-3 pb-3 border-b border-amber-100 dark:border-amber-800">
                      <span className="text-sm font-semibold text-primary-700 dark:text-white">BESS</span>
                      <span className="text-xs font-medium text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/50 px-2.5 py-0.5 rounded-full">Active</span>
                    </div>
                    <div className="space-y-2">
                      {pv.selectedInjection?.bess?.site_name && (
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-gray-400 dark:text-gray-500">Site name</span>
                          <span className="text-xs font-medium text-primary-700 dark:text-white">{pv.selectedInjection.bess.site_name}</span>
                        </div>
                      )}
                      {pv.selectedInjection?.bess?.installation_id && (
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-gray-400 dark:text-gray-500">Installation ID</span>
                          <span className="text-xs font-medium text-primary-700 dark:text-white font-mono">{pv.selectedInjection.bess.installation_id}</span>
                        </div>
                      )}
                      {pv.selectedInjection?.bess?.contract_start_date && (
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-gray-400 dark:text-gray-500">Contract start</span>
                          <span className="text-xs font-medium text-primary-700 dark:text-white">{formatDateToDisplay(pv.selectedInjection.bess.contract_start_date)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {activeEboiler && (
                  <div className="bg-white dark:bg-primary-900 border border-amber-200 dark:border-amber-700/60 rounded-lg p-4 shadow-sm">
                    <div className="flex justify-between items-center mb-3 pb-3 border-b border-amber-100 dark:border-amber-800">
                      <span className="text-sm font-semibold text-primary-700 dark:text-white">E-Boiler</span>
                      <span className="text-xs font-medium text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/50 px-2.5 py-0.5 rounded-full">Active</span>
                    </div>
                    <div className="space-y-2">
                      {pv.selectedInjection?.eboiler?.site_name && (
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-gray-400 dark:text-gray-500">Site name</span>
                          <span className="text-xs font-medium text-primary-700 dark:text-white">{pv.selectedInjection.eboiler.site_name}</span>
                        </div>
                      )}
                      {pv.selectedInjection?.eboiler?.installation_id && (
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-gray-400 dark:text-gray-500">Installation ID</span>
                          <span className="text-xs font-medium text-primary-700 dark:text-white font-mono">{pv.selectedInjection.eboiler.installation_id}</span>
                        </div>
                      )}
                      {pv.selectedInjection?.eboiler?.contract_start_date && (
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-gray-400 dark:text-gray-500">Contract start</span>
                          <span className="text-xs font-medium text-primary-700 dark:text-white">{formatDateToDisplay(pv.selectedInjection.eboiler.contract_start_date)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Contract Specifications */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-primary-700 dark:text-white mb-4">Contract specifications</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-0">
              <div className="flex justify-between items-center py-3 border-b border-gray-200 dark:border-primary-800">
                <span className="text-sm text-gray-500 dark:text-gray-400">Desired start date</span>
                <span className="text-sm font-semibold text-primary-700 dark:text-white">{contract.startDate}</span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-gray-200 dark:border-primary-800">
                <span className="text-sm text-gray-500 dark:text-gray-400">Yuso profit share</span>
                <span className="text-sm font-semibold text-primary-700 dark:text-white">{contract.profitShare}</span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-gray-200 dark:border-primary-800">
                <span className="text-sm text-gray-500 dark:text-gray-400">Yuso monthly fee</span>
                <span className="text-sm font-semibold text-primary-700 dark:text-white">{contract.monthlyFee}</span>
              </div>
            </div>
            {/* Right Column */}
            <div className="space-y-0">
              <div className="flex justify-between items-center py-3 border-b border-gray-200 dark:border-primary-800">
                <span className="text-sm text-gray-500 dark:text-gray-400">Invertor power</span>
                <span className="text-sm font-semibold text-primary-700 dark:text-white">{contract.inverterPower}</span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-gray-200 dark:border-primary-800">
                <span className="text-sm text-gray-500 dark:text-gray-400">Partner choice</span>
                <span className="text-sm font-semibold text-primary-700 dark:text-white">{contract.partner}</span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-gray-200 dark:border-primary-800">
                <span className="text-sm text-gray-500 dark:text-gray-400">Yuso certified controller</span>
                <span className="text-sm font-semibold text-primary-700 dark:text-white">{contract.controller}</span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-gray-200 dark:border-primary-800">
                <span className="text-sm text-gray-500 dark:text-gray-400">Opportunity costs (injection)</span>
                <span className="text-sm font-semibold text-primary-700 dark:text-white">
                  {contract.opportunityCost}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Billing & Payment */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-primary-700 dark:text-white mb-4">Billing & payment</h3>
          <div className="bg-gray-50 dark:bg-primary-800 rounded-lg p-6 border border-gray-200 dark:border-primary-700">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="space-y-2">
                <div className="font-semibold text-base text-primary-700 dark:text-white">{contract.company.name}</div>
                <div className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line">
                  {contract.company.address}
                </div>
                <div className="text-sm text-gray-700 dark:text-gray-300">{contract.company.vat}</div>
                <div className="text-sm text-gray-700 dark:text-gray-300">{contract.company.email}</div>
              </div>
              <div className="flex flex-col justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Billing will occur monthly based on the contract.
                  </p>
                  <div className="mb-4">
                    <div className="text-sm font-semibold text-primary-700 dark:text-white">
                      IBAN {contract.company.iban}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setShowBillingModal(true)}
                  className="bg-primary-100 dark:bg-primary-700 text-primary-700 dark:text-primary-200 px-4 py-2 rounded-lg font-medium hover:bg-primary-200 dark:hover:bg-primary-600 transition-colors w-fit"
                >
                  Change billing
                </button>
              </div>
            </div>
          </div>
        </div>

        <NavigationButtons
          currentStep={4}
          totalSteps={4}
          onPrevious={handlePrevious}
          onSubmit={handlePreviewContract}
          submitText={isPreviewLoading ? "Processing..." : "Preview Contract"}
          submitDisabled={isPreviewLoading}
        />
      </div>

      {/* Billing Edit Modal */}
      <BillingEditModal
        showModal={showBillingModal}
        onClose={() => setShowBillingModal(false)}
        editingBillingInfo={getBillingInfoFromCompany()}
        selectedUserCompany={company}
        onSave={handleBillingSave}
      />

      {/* Contract Preview Modal */}
      <ContractPreviewModal
        isOpen={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        onAccept={handleSignContract}
        contractData={contract}
        signerName={`${user?.user?.firstName || ""} ${user?.user?.lastName || ""}`.trim() || "JOHN DOE"}
        pdfUrl={pdfUrl}
        hasCombinedServices={hasCombinedServices}
        signBlockedReason={signBlockedReason}
      />

      {/* PV Curtailment Success Modal */}
      {signedContractSnapshot && (
        <PvCurtailmentSuccessModal
          showModal={showSuccessModal}
          onClose={() => setShowSuccessModal(false)}
          siteName={signedContractSnapshot.siteName}
          companyName={signedContractSnapshot.companyName}
          startDate={signedContractSnapshot.startDate}
          endDate={signedContractSnapshot.endDate}
          monthlyFee={signedContractSnapshot.monthlyFee}
          inverterPower={signedContractSnapshot.inverterPower}
          partner={signedContractSnapshot.partner}
          controller={signedContractSnapshot.controller}
          opportunityCost={signedContractSnapshot.opportunityCost}
          profitShare={signedContractSnapshot.profitShare}
        />
      )}

      {/* Contract Generation Animation Overlay */}
      {isPreviewLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-primary-900 rounded-2xl shadow-2xl p-10 max-w-md w-full mx-4">
            <div className="flex flex-col items-center">
              {/* Animated Document Icon */}
              <div className="relative mb-8">
                <div className="absolute inset-0 bg-primary-200 rounded-full animate-ping opacity-20"></div>
                <div className="relative bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-800 dark:to-primary-700 p-8 rounded-full">
                  <svg
                    className="w-16 h-16 text-primary-600 dark:text-primary-300 animate-pulse"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
              </div>

              <h3 className="text-xl font-semibold text-primary-700 dark:text-white mb-3">
                Generating Your Contract
              </h3>

              {/* Progress Steps */}
              <div className="space-y-3 mb-6 w-full max-w-xs">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-primary-700 dark:text-primary-300">
                      Submitting contract details
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-primary-100 dark:bg-primary-700 rounded-full flex items-center justify-center animate-pulse">
                      <div className="w-4 h-4 bg-primary-600 dark:bg-primary-400 rounded-full animate-ping"></div>
                    </div>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-primary-600 dark:text-primary-400">
                      Generating PDF documents
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-3 opacity-50">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 border-2 border-gray-300 dark:border-primary-600 rounded-full flex items-center justify-center">
                      <span className="text-xs text-gray-500 dark:text-gray-400">3</span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-500 dark:text-gray-400">Preparing for signature</p>
                  </div>
                </div>
              </div>

              {/* Loading Bar */}
              <div className="w-full max-w-xs bg-gray-200 dark:bg-primary-700 rounded-full h-2 mb-4 overflow-hidden">
                <div className="bg-gradient-to-r from-primary-400 to-primary-600 h-full rounded-full animate-loading-bar"></div>
              </div>

              <p className="text-sm text-primary-500 dark:text-primary-400 animate-pulse">
                This may take a few moments...
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ContractProp;
